import { useState } from "react";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";

interface ItemType {
  value: string;
  img?: string;
  tooltip?: string;
}

interface PropType {
  items: ItemType[];
  selectedValue?: string;
  setSelectedValue: (value: string) => void;
  toolTip?: string;
  selectedImages?: string;
  multiple?: boolean;
  selectedItems?: string[];
  setSelectedItems?: (value: string[] | ((prev: string[]) => string[])) => void;
}

const ImageSelectList = ({
  items,
  selectedValue,
  setSelectedValue,
  toolTip,
  selectedImages,
  multiple = false,
  setSelectedItems,
  selectedItems,
}: PropType) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const handleToggleDropdown = () => {
    setOpen((prev) => !prev);
  };

  const handleSelectItem = (value: string) => {
    setSelectedValue(value);
    if (multiple && setSelectedItems) {
      setSelectedItems((prev: string[]) => {
        if (!prev) return [value];
        return prev.includes(value)
          ? prev.filter((item) => item !== value)
          : [...prev, value];
      });
    } else {
      setOpen(false);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative", display: "inline-block" }}>
        {/* Dropdown toggle */}
        <Box onClick={handleToggleDropdown}>
          <Tooltip title={toolTip} placement="top" arrow>
            {items[0]?.img ? (
              <Box
                component="img"
                src={
                  selectedImages ||
                  items.find((item) => item.value === selectedValue)?.img
                }
                alt="lang"
                sx={{
                  display: "flex",
                  height: 40,
                  width: 40,
                  objectFit: "cover",
                  borderRadius: "4px",
                  cursor: "pointer",
                  transition: "background 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                  },
                  padding: "5px",
                }}
              />
            ) : (
              <Box
                sx={{
                  height: 40,
                  width: 40,
                  objectFit: "cover",
                  borderRadius: "4px",
                  cursor: "pointer",
                  transition: "background 0.3s ease",
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                  },
                  padding: "5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {t(selectedValue?.toUpperCase() || "")}
              </Box>
            )}
          </Tooltip>
        </Box>

        {/* Dropdown list */}
        {open && (
          <Box
            sx={{
              position: "absolute",
              top: 40,
              left: -5,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "8px",
              padding: "8px",
              zIndex: 10,
              marginTop: 1,
            }}
          >
            {items.map((item) => (
              <Box key={item.value}>
                <Tooltip title={t(item?.tooltip || "")} placement="left" arrow>
                  <Box
                    onClick={() => handleSelectItem(item.value)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      borderRadius: "4px",
                      marginBottom: "4px",
                      padding: "5px",
                      transition: "background 0.3s ease",
                      backgroundColor: multiple
                        ? selectedItems?.includes(item.value)
                          ? "rgba(0, 0, 255, 0.1)"
                          : "transparent"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.2)",
                      },
                      "&:last-child": {
                        marginBottom: 0,
                      },
                      objectFit: "cover",
                    }}
                  >
                    {item.img ? (
                      <Box
                        component="img"
                        src={item.img}
                        alt="lang"
                        sx={{
                          objectFit: "cover",
                          borderRadius: "4px",
                          height: 25,
                          width: 25,
                        }}
                      />
                    ) : (
                      <Box>{t(item?.value?.toUpperCase())}</Box>
                    )}
                  </Box>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default ImageSelectList;
